/**
 * Coming Soon Page
 * Standalone landing page with countdown timer
 * Fetches settings from backend including countdown date
 */
import React, { useState, useEffect } from 'react';
import axios from '../lib/axios';
import { API } from '../App';

// Exact assets from Hero section
const HERO_IMAGE_LIGHT = 'https://res.cloudinary.com/dtqazyw6v/image/upload/f_auto,q_auto,w_1920/yourbuysitehero/hero_light_willemstad';
const HERO_IMAGE_DARK = 'https://res.cloudinary.com/dtqazyw6v/image/upload/f_auto,q_auto,w_1920/yourbuysitehero/hero_dark_willemstad';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/1uy6w34w_logoparadiseNOBACK.png';

const ComingSoonPage = () => {
  const [settings, setSettings] = useState({
    title: 'Coming Soon',
    subtitle: "We're working hard to bring you something amazing. Stay tuned!"
  });
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [loading, setLoading] = useState(true);

  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        if (response.data) {
          setSettings({
            title: response.data.title || 'Coming Soon',
            subtitle: response.data.subtitle || "We're working hard to bring you something amazing. Stay tuned!",
            countdown_date: response.data.countdown_date
          });
        }
      } catch (error) {
        console.error('Error fetching coming soon settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Calculate countdown
  useEffect(() => {
    if (!settings.countdown_date) {
      // Default: 48 hours from now if no date set
      const defaultTarget = new Date();
      defaultTarget.setHours(defaultTarget.getHours() + 48);
      settings.countdown_date = defaultTarget.toISOString();
    }

    // Parse the date - if it's in format "YYYY-MM-DDTHH:mm:ss" without timezone, 
    // treat it as AST (UTC-4) time
    let targetDate;
    const dateStr = settings.countdown_date;
    
    if (dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
      // Has timezone info, parse directly
      targetDate = new Date(dateStr);
    } else {
      // No timezone - treat as AST (UTC-4)
      // Add the AST offset to make it work correctly
      targetDate = new Date(dateStr + '-04:00');
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [settings.countdown_date]);

  // Format number with leading zero
  const formatNumber = (num) => num.toString().padStart(2, '0');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Light Mode Background */}
      <div className="absolute inset-0 dark:hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_IMAGE_LIGHT}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
      </div>
      
      {/* Dark Mode Background */}
      <div className="absolute inset-0 hidden dark:block">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_IMAGE_DARK}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
      </div>
      
      {/* Content Container */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center">
        
        {/* Logo - exact same as hero */}
        <img 
          src={LOGO_URL} 
          alt="YourBuySite" 
          className="w-72 md:w-96 lg:w-[450px] h-auto mb-8 drop-shadow-2xl"
        />
        
        {/* Coming Soon Text */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
          {settings.title}
        </h1>
        
        <p className="text-lg md:text-xl text-white/80 mb-12 max-w-md">
          {settings.subtitle}
        </p>
        
        {/* Countdown Timer */}
        <div className="flex items-center gap-3 md:gap-4 lg:gap-6">
          {/* Days - only show if > 0 */}
          {timeLeft.days > 0 && (
            <>
              <div className="flex flex-col items-center">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 md:p-4 lg:p-6 min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
                  <span className="text-3xl md:text-4xl lg:text-6xl font-bold text-white tabular-nums">
                    {formatNumber(timeLeft.days)}
                  </span>
                </div>
                <span className="text-white/60 text-xs md:text-sm lg:text-base mt-2 uppercase tracking-wider">
                  Days
                </span>
              </div>
              <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-white/50 -mt-6">:</span>
            </>
          )}
          
          {/* Hours */}
          <div className="flex flex-col items-center">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 md:p-4 lg:p-6 min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
              <span className="text-3xl md:text-4xl lg:text-6xl font-bold text-white tabular-nums">
                {formatNumber(timeLeft.hours)}
              </span>
            </div>
            <span className="text-white/60 text-xs md:text-sm lg:text-base mt-2 uppercase tracking-wider">
              Hours
            </span>
          </div>
          
          {/* Separator */}
          <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-white/50 -mt-6">:</span>
          
          {/* Minutes */}
          <div className="flex flex-col items-center">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 md:p-4 lg:p-6 min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
              <span className="text-3xl md:text-4xl lg:text-6xl font-bold text-white tabular-nums">
                {formatNumber(timeLeft.minutes)}
              </span>
            </div>
            <span className="text-white/60 text-xs md:text-sm lg:text-base mt-2 uppercase tracking-wider">
              Minutes
            </span>
          </div>
          
          {/* Separator */}
          <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-white/50 -mt-6">:</span>
          
          {/* Seconds */}
          <div className="flex flex-col items-center">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-3 md:p-4 lg:p-6 min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
              <span className="text-3xl md:text-4xl lg:text-6xl font-bold text-white tabular-nums">
                {formatNumber(timeLeft.seconds)}
              </span>
            </div>
            <span className="text-white/60 text-xs md:text-sm lg:text-base mt-2 uppercase tracking-wider">
              Seconds
            </span>
          </div>
        </div>
        
        {/* Social Links */}
        <div className="mt-16 flex flex-col items-center gap-4">
          <p className="text-white/60 text-sm">
            Follow us for updates
          </p>
          <div className="flex items-center gap-4">
            <a 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://instagram.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>
        
      </div>
      
      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
    </div>
  );
};

export default ComingSoonPage;
